<template>
  <button
    class="nc-btn"
    :class="[
      icon ? 'tw-rounded-full' : 'tw-rounded-lg',
      sizeClasses,
      fillClasses,
      expand === 'block' ? 'tw-w-full' : '',
      (disabled || loading) ? 'tw-opacity-40 tw-pointer-events-none' : '',
    ]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <NcSpinner v-if="loading" size="sm" :color="spinnerColor" />
    <template v-else>
      <span v-if="$slots.start" class="tw-flex tw-items-center">
        <slot name="start" />
      </span>
      <slot />
    </template>
  </button>
</template>

<script>
import NcSpinner from './NcSpinner.vue';

const fillColorMap = {
  solid: {
    primary: 'nc-btn--solid-primary',
    danger: 'nc-btn--solid-danger',
    medium: 'nc-btn--solid-medium',
    success: 'nc-btn--solid-success',
  },
  outline: {
    primary: 'nc-btn--outline-primary',
    danger: 'nc-btn--outline-danger',
    medium: 'nc-btn--outline-medium',
  },
  clear: {
    primary: 'nc-btn--clear-primary',
    danger: 'nc-btn--clear-danger',
    medium: 'nc-btn--clear-medium',
  },
};

const sizeMap = {
  sm: 'nc-btn--sm',
  md: 'nc-btn--md',
  lg: 'nc-btn--lg',
};

const iconSizeMap = {
  sm: 'nc-btn--icon-sm',
  md: 'nc-btn--icon-md',
  lg: 'nc-btn--icon-lg',
};

export default {
  name: 'NcButton',
  components: { NcSpinner },
  props: {
    color: { type: String, default: 'primary' },
    fill: { type: String, default: 'solid', validator: v => ['solid', 'outline', 'clear'].includes(v) },
    size: { type: String, default: 'md', validator: v => ['sm', 'md', 'lg'].includes(v) },
    expand: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    icon: { type: Boolean, default: false },
  },
  emits: ['click'],
  computed: {
    sizeClasses() {
      return this.icon ? iconSizeMap[this.size] : sizeMap[this.size];
    },
    fillClasses() { return fillColorMap[this.fill]?.[this.color] || fillColorMap[this.fill]?.primary || ''; },
    spinnerColor() {
      if (this.fill === 'solid') return 'white';
      return 'primary';
    },
  },
};
</script>

<style scoped>
.nc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: var(--nc-font-weight-medium);
  transition: all var(--nc-transition-fast);
  cursor: pointer;
  border: none;
  background: transparent;
  color: inherit;
  font-family: var(--nc-font-family);
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
  line-height: 1;
  position: relative;
}
/* Garandeer een tikgebied van minimaal 44px (iOS HIG / WCAG 2.5.5) ook bij
   de visueel kleinere icon-varianten (28/36px), zonder het icoon zelf groter
   te maken. Een transparant pseudo-element vergroot het klikvlak; klikken
   erop tellen als klikken op de knop. */
.nc-btn::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  min-width: 44px;
  min-height: 44px;
}
.nc-btn :deep(svg) {
  display: block;
  vertical-align: middle;
}
.nc-btn:active {
  transform: scale(0.97);
}

/* Sizes */
.nc-btn--sm { font-size: var(--nc-font-size-xs); padding: 6px 12px; min-height: 32px; }
.nc-btn--md { font-size: var(--nc-font-size-sm); padding: 8px 16px; min-height: 40px; }
.nc-btn--lg { font-size: var(--nc-font-size-sm); padding: 10px 20px; min-height: 44px; }
.nc-btn--icon-sm { width: 28px; height: 28px; padding: 0; }
.nc-btn--icon-md { width: 36px; height: 36px; padding: 0; }
.nc-btn--icon-lg { width: 44px; height: 44px; padding: 0; }

/* Solid fills */
.nc-btn--solid-primary {
  background: var(--nc-color-primary);
  color: var(--nc-text-on-primary);
  box-shadow: var(--nc-shadow-sm);
}
.nc-btn--solid-primary:hover { background: var(--nc-color-primary-hover); }

.nc-btn--solid-danger {
  background: var(--nc-color-danger);
  color: var(--nc-text-on-primary);
  box-shadow: var(--nc-shadow-sm);
}
.nc-btn--solid-danger:hover { background: #d43d51; }

.nc-btn--solid-medium {
  background: var(--nc-color-light);
  color: var(--nc-text);
}
.nc-btn--solid-medium:hover { background: var(--nc-color-light-shade); }

.nc-btn--solid-success {
  background: var(--nc-color-success);
  color: var(--nc-text-on-primary);
  box-shadow: var(--nc-shadow-sm);
}
.nc-btn--solid-success:hover { background: #12a050; }

/* Outline fills */
.nc-btn--outline-primary {
  border: 1px solid var(--nc-color-primary-light);
  color: var(--nc-color-primary);
  background: transparent;
}
.nc-btn--outline-primary:hover { background: var(--nc-color-primary-light); }

.nc-btn--outline-danger {
  border: 1px solid var(--nc-color-danger-light);
  color: var(--nc-color-danger);
  background: transparent;
}
.nc-btn--outline-danger:hover { background: var(--nc-color-danger-light); }

.nc-btn--outline-medium {
  border: 1px solid var(--nc-border);
  color: var(--nc-color-medium);
  background: transparent;
}
.nc-btn--outline-medium:hover { background: var(--nc-color-light); }

/* Clear fills */
.nc-btn--clear-primary {
  color: var(--nc-color-primary);
  background: transparent;
}
.nc-btn--clear-primary:hover { background: var(--nc-color-primary-light); }

.nc-btn--clear-danger {
  color: var(--nc-color-danger);
  background: transparent;
  opacity: 0.8;
}
.nc-btn--clear-danger:hover {
  opacity: 1;
  background: var(--nc-color-danger-light);
}

.nc-btn--clear-medium {
  color: var(--nc-color-medium);
  background: transparent;
}
.nc-btn--clear-medium:hover {
  color: var(--nc-text);
  background: var(--nc-color-light);
}
</style>
